/**
 * Draft state for the FilterSheet's optional LISTS group (SE-8627).
 *
 * The list selection is not part of `FilterState` — it scopes a host's
 * candidate pool by watchlist membership rather than filtering a player
 * attribute, so each host owns its committed selection (the pipeline since
 * SE-7094, the scenario pitch's "Add players to" panel since SE-8627). This
 * hook gives it the same draft semantics every other section has: edits stay
 * local until Show Results commits them, Cancel discards them, CLEAR ALL
 * resets them, and reopening the sheet re-snapshots the committed selection.
 * @module features/scouting/hooks/useListsFilterDraft
 */

import { useCallback, useMemo, useState } from "react";

import type { FilterOption } from "@/features/scouting/components/molecules/FilterChip";
import type { ListsFilterSectionProps } from "@/features/scouting/components/molecules/ListsFilterSection";
import toggleId from "@/utils/toggleId";

/** What a host hands the FilterSheet to offer the LISTS group. */
export interface ListsFilterSource {
  /** The lists on offer, as {id, label}. */
  readonly options: readonly FilterOption[];
  /** The committed selection. */
  readonly selection: readonly string[];
  /** Commit the draft selection (Show Results). */
  readonly onCommit: (selection: readonly string[]) => void;
}

/** Parameters for {@link useListsFilterDraft}. */
interface UseListsFilterDraftParams {
  /** The host's lists; undefined when the host offers no LISTS group. */
  readonly lists?: ListsFilterSource;
  /** Whether the sheet is open — a reopen re-snapshots the draft. */
  readonly isOpen: boolean;
  /** The panel's open chip id — LISTS shares the one-open-chip state. */
  readonly openChipId: string | null;
  /** Setter for the panel's open chip id. */
  readonly setOpenChipId: (
    next: string | null | ((prev: string | null) => string | null)
  ) => void;
  /** Whether options are still loading. */
  readonly loading: boolean;
}

/** The chip id the LISTS chip claims in the panel's open-chip state. */
const LIST_CHIP_ID = "list";

/** No committed selection — the stable default when no lists are passed. */
const NONE: readonly string[] = [];

/**
 * Own the LISTS draft for a filter sheet.
 * @param params - See {@link UseListsFilterDraftParams}.
 * @param params.lists - The host's lists, or undefined.
 * @param params.isOpen - Whether the sheet is open.
 * @param params.openChipId - The panel's open chip id.
 * @param params.setOpenChipId - Setter for the open chip id.
 * @param params.loading - Whether options are loading.
 * @returns The section props (undefined without lists), the draft count for
 *   CLEAR ALL, and commit / clear actions for the sheet's footer and header.
 */
const useListsFilterDraft = ({
  lists,
  isOpen,
  openChipId,
  setOpenChipId,
  loading,
}: UseListsFilterDraftParams) => {
  const committed = lists?.selection ?? NONE;
  const [draft, setDraft] = useState<readonly string[]>(committed);
  const [isExpanded, setIsExpanded] = useState(false);

  // Re-snapshot on the open transition — adjusted during render (the React
  // pattern for resetting state on a prop change) rather than in an effect.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) setDraft(committed);
  }

  const onToggle = useCallback(
    (id: string) => setDraft(current => toggleId(current, id)),
    []
  );
  const clear = useCallback(() => setDraft(NONE), []);
  const onToggleSection = useCallback(
    () => setIsExpanded(expanded => !expanded),
    []
  );
  const onToggleChip = useCallback(
    () => setOpenChipId(prev => (prev === LIST_CHIP_ID ? null : LIST_CHIP_ID)),
    [setOpenChipId]
  );
  const onCloseChip = useCallback(() => setOpenChipId(null), [setOpenChipId]);

  const onCommit = lists?.onCommit;
  const commit = useCallback(() => onCommit?.(draft), [onCommit, draft]);

  const options = lists?.options;
  const section = useMemo<ListsFilterSectionProps | undefined>(
    () =>
      options
        ? {
            options,
            selectedIds: draft,
            onToggle,
            onClear: clear,
            isExpanded,
            onToggleSection,
            isChipOpen: openChipId === LIST_CHIP_ID,
            onToggleChip,
            onCloseChip,
            loading,
          }
        : undefined,
    [
      options,
      draft,
      onToggle,
      clear,
      isExpanded,
      onToggleSection,
      openChipId,
      onToggleChip,
      onCloseChip,
      loading,
    ]
  );

  return {
    section,
    draftCount: lists ? draft.length : 0,
    commit,
    clear,
  };
};

export default useListsFilterDraft;
